import { IconCheck, IconCircleDashed } from "@tabler/icons-react";
import { Badge, Card, Divider, Group, Heading, Loader, Stack, Text, ThemeIcon } from "@/components/ui";
import type { RequisitosDeSolicitud, TarjetaRequisito } from "@/features/documentos/services/documentos.api";
import { documentosDeTarjeta } from "@/features/documentos/utils/documento-helpers";
import type { DocumentoVinculado } from "@/lib/types";
import { DocumentoRow } from "./DocumentoRow";
import { textoCantidad } from "./RequisitoCard";

interface Props {
  requisitos: RequisitosDeSolicitud | undefined;
  documentos: DocumentoVinculado[];
  solicitudId: string;
}

/**
 * Gestión Documental (fase 2) · resumen del expediente contra los REQUISITOS configurados del
 * tipo/subtipo (sustituye al checklist fijo FB60/Invoice/Contrapago que vivía en código). Lista
 * cada requisito con su estado; para la factura que registra el servidor (XML/PDF del CFDI, PDF
 * del Invoice) muestra además sus archivos con acciones (no tienen tarjeta de carga).
 */
export function ChecklistResumen({ requisitos, documentos, solicitudId }: Props) {
  if (!requisitos) {
    return (
      <Card padding="lg">
        <Group gap="xs">
          <Loader size="xs" color="multiva" />
          <Text size="sm" c="dimmed">
            Cargando los requisitos documentales…
          </Text>
        </Group>
      </Card>
    );
  }

  const { tarjetas } = requisitos;
  return (
    <Card padding="lg">
      <Stack gap="md">
        <div>
          <Group gap={6} align="baseline">
            <Heading order={5}>Requisitos documentales</Heading>
            <Badge variant="light" color={requisitos.completo ? "multiva" : "advertencia"}>
              {requisitos.completo ? "Completo" : "Incompleto"}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed" className="mt-0.5">
            {tarjetas.length === 0
              ? "Este tipo de solicitud no tiene requisitos documentales configurados."
              : "Configurados por el administrador para este tipo y subtipo de solicitud."}
          </Text>
          {requisitos.faltantes.length > 0 && (
            <Stack gap={2} className="mt-2">
              {requisitos.faltantes.map((f) => (
                <Text key={f} size="xs" c="advertencia-700">
                  • {f}
                </Text>
              ))}
            </Stack>
          )}
        </div>
        {tarjetas.length > 0 && (
          <>
            <Divider />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {tarjetas.map((t) => (
                <RequisitoItem
                  key={`${t.requisitoId}-${t.numeroCfdi ?? 0}`}
                  tarjeta={t}
                  docs={t.esDelServidor ? documentosDeTarjeta(t, tarjetas, documentos) : []}
                  solicitudId={solicitudId}
                />
              ))}
            </div>
          </>
        )}
      </Stack>
    </Card>
  );
}

function RequisitoItem({ tarjeta, docs, solicitudId }: { tarjeta: TarjetaRequisito; docs: DocumentoVinculado[]; solicitudId: string }) {
  const color = tarjeta.cumple ? "multiva" : "advertencia";
  const etiqueta =
    (tarjeta.tipoDocumento ? `${tarjeta.categoriaNombre} · ${tarjeta.tipoDocumento}` : tarjeta.categoriaNombre) +
    (tarjeta.porCfdi && tarjeta.numeroCfdi ? ` · CFDI ${tarjeta.numeroCfdi}` : "");
  return (
    <div
      className="rounded-lg p-2.5"
      style={{ border: `1px solid var(--color-${color}-300)`, background: `color-mix(in srgb, var(--color-${color}-600) 6%, transparent)` }}
      title={textoCantidad(tarjeta)}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap" className="min-w-0">
          <ThemeIcon size="sm" variant="filled" color={color}>
            {tarjeta.cumple ? <IconCheck size={12} stroke={2} /> : <IconCircleDashed size={12} stroke={1.7} />}
          </ThemeIcon>
          <Text size="sm" fw={600} truncate>
            {etiqueta}
          </Text>
        </Group>
        <Group gap={4} wrap="nowrap">
          {docs.map((d) => (
            <DocumentoRow key={d.id} documento={d} solicitudId={solicitudId} puedeEliminar={false} compacto />
          ))}
          <Text size="xs" c="dimmed">
            ({tarjeta.cargados}
            {tarjeta.maximo ? `/${tarjeta.maximo}` : ""})
          </Text>
        </Group>
      </Group>
    </div>
  );
}
